import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { SmsSenderSummary } from 'compositions/SenderSelector'
import {
  useAddScheduledCommunicationContact,
  useAddScheduledCommunicationContactGroup,
  useDeleteScheduledCommunication,
  useRemoveScheduledCommunicationContact,
  useRemoveScheduledCommunicationContactGroup,
  useScheduledCommunication,
  useUpdateScheduledCommunication,
} from 'hooks/api'
import { Pencil, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { ScheduledCommunicationDetail as ScheduledCommunicationDetailType } from 'types/communications'

import { type CommunicationFormData, CommunicationFormFields } from './communication-form-fields'
import type { Recipient } from './recipient-selector'
function communicationToFormData(comm: ScheduledCommunicationDetailType): CommunicationFormData {
  return {
    name: comm.name,
    method: comm.method,
    subject: comm.subject ?? '',
    templateId: comm.templateId ?? '',
    topicId: comm.topicId ?? '',
    senderId: comm.senderId ?? '',
  }
}

function communicationToRecipients(comm: ScheduledCommunicationDetailType): Recipient[] {
  const contacts: Recipient[] = comm.contacts.map((c) => ({
    type: 'contact',
    id: c.contact.id,
    label: [c.contact.firstName, c.contact.lastName].filter(Boolean).join(' ') || c.contact.email || '(unnamed)',
  }))
  const groups: Recipient[] = comm.contactGroups.map((g) => ({
    type: 'group',
    id: g.contactGroup.id,
    label: g.contactGroup.name,
  }))
  return [...contacts, ...groups]
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

function CommunicationDetailContent({
  communication,
  refetch,
}: {
  communication: ScheduledCommunicationDetailType | null
  refetch: () => void
}) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateCommunication } = useUpdateScheduledCommunication()
  const { perform: deleteCommunication } = useDeleteScheduledCommunication()
  const { perform: addContact } = useAddScheduledCommunicationContact()
  const { perform: removeContact } = useRemoveScheduledCommunicationContact()
  const { perform: addContactGroup } = useAddScheduledCommunicationContactGroup()
  const { perform: removeContactGroup } = useRemoveScheduledCommunicationContactGroup()

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<CommunicationFormData>({
    name: '',
    method: 'EMAIL',
    subject: '',
    templateId: '',
    topicId: '',
    senderId: '',
  })
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [originalRecipients, setOriginalRecipients] = useState<Recipient[]>([])

  useEffect(() => {
    if (communication) {
      setFormData(communicationToFormData(communication))
      const r = communicationToRecipients(communication)
      setRecipients(r)
      setOriginalRecipients(r)
    }
  }, [communication])

  const handleFieldChange = useCallback((field: keyof CommunicationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleAddRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => [...prev, recipient])
  }, [])

  const handleRemoveRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => prev.filter((r) => !(r.type === recipient.type && r.id === recipient.id)))
  }, [])

  const startEditing = useCallback(() => {
    if (communication) {
      setFormData(communicationToFormData(communication))
      const r = communicationToRecipients(communication)
      setRecipients(r)
      setOriginalRecipients(r)
    }
    setEditing(true)
  }, [communication])

  const cancelEditing = useCallback(() => {
    if (communication) {
      setFormData(communicationToFormData(communication))
      setRecipients(communicationToRecipients(communication))
    }
    setEditing(false)
  }, [communication])

  const handleSave = useCallback(async () => {
    if (!id) {
      return
    }
    setSaving(true)
    try {
      await updateCommunication(id, {
        name: formData.name,
        method: formData.method,
        subject: formData.method === 'EMAIL' ? formData.subject || undefined : undefined,
        templateId: formData.templateId || null,
        topicId: formData.topicId || null,
        senderId: formData.senderId || null,
      })

      const originalKeys = new Set(originalRecipients.map((r) => `${r.type}:${r.id}`))
      const currentKeys = new Set(recipients.map((r) => `${r.type}:${r.id}`))

      const toAdd = recipients.filter((r) => !originalKeys.has(`${r.type}:${r.id}`))
      const toRemove = originalRecipients.filter((r) => !currentKeys.has(`${r.type}:${r.id}`))

      await Promise.all([
        ...toAdd.filter((r) => r.type === 'contact').map((r) => addContact(id, r.id)),
        ...toAdd.filter((r) => r.type === 'group').map((r) => addContactGroup(id, r.id)),
        ...toRemove.filter((r) => r.type === 'contact').map((r) => removeContact(id, r.id)),
        ...toRemove.filter((r) => r.type === 'group').map((r) => removeContactGroup(id, r.id)),
      ])

      refetch()
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }, [
    id,
    formData,
    recipients,
    originalRecipients,
    updateCommunication,
    addContact,
    addContactGroup,
    removeContact,
    removeContactGroup,
    refetch,
  ])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    await deleteCommunication(id)
    navigate('/communications')
  }, [id, deleteCommunication, navigate])

  if (!communication) {
    return <div className="py-12 text-center text-muted-foreground">Communication not found</div>
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CardTitle>{communication.name}</CardTitle>
          <Badge variant={communication.active ? 'default' : 'secondary'} className="text-xs">
            {communication.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={startEditing}>
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
              <ConfirmDialog
                title="Delete communication"
                description={`Are you sure you want to delete "${communication.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                trigger={
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-1 h-4 w-4" />
                    Delete
                  </Button>
                }
              />
            </>
          )}
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-6">
        {editing ? (
          <CommunicationFormFields
            data={formData}
            onChange={handleFieldChange}
            recipients={recipients}
            onAddRecipient={handleAddRecipient}
            onRemoveRecipient={handleRemoveRecipient}
          />
        ) : (
          <dl className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InfoRow label="Delivery Method">
                <Badge variant="secondary" className="text-xs">
                  {communication.method}
                </Badge>
              </InfoRow>
              {communication.subject && <InfoRow label="Subject">{communication.subject}</InfoRow>}
            </div>
            {communication.method === 'SMS' ? (
              <InfoRow label="SMS sender">
                <SmsSenderSummary senderId={communication.senderId ?? ''} />
              </InfoRow>
            ) : null}

            {/* Recipients display */}
            <InfoRow label="Recipients">
              {communication.contacts.length === 0 && communication.contactGroups.length === 0 ? (
                <span className="text-muted-foreground">None</span>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {communication.contacts.map((c) => {
                    const name =
                      [c.contact.firstName, c.contact.lastName].filter(Boolean).join(' ') ||
                      c.contact.email ||
                      '(unnamed)'
                    return (
                      <span
                        key={c.id}
                        className="inline-flex items-center rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                        {name}
                      </span>
                    )
                  })}
                  {communication.contactGroups.map((g) => (
                    <span
                      key={g.id}
                      className="inline-flex items-center rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                      <span className="mr-1 opacity-60">Group:</span>
                      {g.contactGroup.name}
                    </span>
                  ))}
                </div>
              )}
            </InfoRow>
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

export function CommunicationDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: communication, loading, refetch } = useScheduledCommunication(id)

  if (!loading && !communication) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Communication not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/communications">Back to Communications</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        }>
        <CommunicationDetailContent communication={communication} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
